import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { GalleryItemView } from '../../../../features/projects/interfaces/project.interface';
import { GalleryAnalyticsService } from '../../../services/gallery-analytics.service';
import { ScrollLockService } from '../../../services/scroll-lock.service';

export const LIGHTBOX_IMAGE_SIZES = '(min-width: 1300px) 1300px, 92vw';

const PRELOAD_ALL_THRESHOLD = 8;
const BACKDROP_CLOSE_DELAY_MS = 400;
const MIN_IMAGE_SCALE = 1;
const MAX_IMAGE_SCALE = 4;

type SlideDirection = 'prev' | 'next' | 'none';

@Component({
  selector: 'app-lightbox',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lightbox.component.html',
  styleUrl: './lightbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LightboxComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() items: GalleryItemView[] = [];
  @Input() activeIndex = 0;
  @Input() showThumbnails = false;
  @Input() accentColor: string | null = null;
  @Input() analyticsLayout = 'unknown';
  @Input() autoplay = false;
  @Input() autoplayInterval = 5000;
  @Output() closeLightbox = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();

  @ViewChild('dialogContainer') dialogContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('closeButton') closeButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('stageCanvas') stageCanvasRef!: ElementRef<HTMLElement>;
  @ViewChild('thumbsNav') thumbsNavRef?: ElementRef<HTMLElement>;

  readonly imageSizes = LIGHTBOX_IMAGE_SIZES;

  public fading = false;
  public isFullscreen = false;
  public immersiveMode = false;
  public imageLoading = false;
  public imageScale = 1;
  public slideDirection: SlideDirection = 'none';
  public isAutoplaying = false;
  public slideAnnouncement = '';

  public readonly fullscreenApiSupported: boolean;

  private pendingIndex: number | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private pinchStartDistance = 0;
  private pinchStartScale = 1;
  private previouslyFocusedElement: HTMLElement | null = null;
  private openedAt = 0;
  private focusTrap?: FocusTrap;
  private readonly inertedElements: Element[] = [];
  private lastTrackedSlideIndex = -1;
  private autoplayTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly scrollLock: ScrollLockService,
    private readonly focusTrapFactory: FocusTrapFactory,
    private readonly analytics: GalleryAnalyticsService
  ) {
    const probe = document.createElement('div');
    this.fullscreenApiSupported = !!(
      probe.requestFullscreen ??
      (probe as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen
    );
  }

  public get lightboxItem(): GalleryItemView | null {
    return this.items[this.activeIndex] ?? null;
  }

  public get lightboxTitle(): string {
    const title = String(this.lightboxItem?.title ?? '').trim();
    const slideNumberPlaceholder = String(this.activeIndex + 1);

    if (!title || title === slideNumberPlaceholder) {
      return '';
    }

    return title;
  }

  public get hasMultipleItems(): boolean {
    return this.items.length > 1;
  }

  public get showFullscreenControl(): boolean {
    return this.fullscreenApiSupported || true;
  }

  public get useDirectionalSlide(): boolean {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  public get panelStyles(): Record<string, string> {
    const accent = this.accentColor?.trim();
    return accent ? { '--lb-accent': accent } : {};
  }

  public get imageTransform(): string {
    return this.imageScale > 1 ? `scale(${this.imageScale})` : 'none';
  }

  ngAfterViewInit(): void {
    this.openedAt = Date.now();
    this.scrollLock.lock();
    this.enablePageInert();
    this.focusTrap = this.focusTrapFactory.create(this.dialogContainerRef.nativeElement);
    this.focusTrap.attachAnchors();

    this.previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setTimeout(() => this.focusTrap?.focusInitialElement() ?? this.closeButtonRef?.nativeElement.focus());

    this.updateSlideAnnouncement();
    this.resetImageLoadingState();
    this.preloadImages();
    this.trackSlideView(true);
    this.scrollActiveThumbIntoView();
    if (this.autoplay) {
      this.startAutoplay();
    }
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeIndex'] || changes['items']) {
      this.activeIndex = this.normalizeIndex(this.activeIndex);

      if (changes['activeIndex'] && !changes['activeIndex'].firstChange) {
        this.pauseAllVideos();
        this.resetImageLoadingState();
        this.resetImageZoom();
        this.trackSlideView(false);
      }

      this.preloadImages();
      this.updateSlideAnnouncement();
      setTimeout(() => this.scrollActiveThumbIntoView());
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
    this.exitFullscreenIfActive();
    this.immersiveMode = false;
    this.focusTrap?.destroy();
    this.disablePageInert();
    this.scrollLock.unlock();
    this.previouslyFocusedElement?.focus();
  }

  public onBackdropClick(): void {
    if (Date.now() - this.openedAt < BACKDROP_CLOSE_DELAY_MS) {
      return;
    }

    this.close();
  }

  public toggleFullscreen(): void {
    if (!this.fullscreenApiSupported) {
      this.immersiveMode = !this.immersiveMode;
      this.isFullscreen = this.immersiveMode;
      this.analytics.track(this.immersiveMode ? 'fullscreen_enter' : 'fullscreen_exit', {
        galleryLayout: this.analyticsLayout,
        slideIndex: this.activeIndex,
        slideCount: this.items.length,
      });
      this.cdr.markForCheck();
      return;
    }

    const stage = this.stageCanvasRef?.nativeElement;
    if (!stage) {
      return;
    }

    if (this.isStageFullscreen()) {
      this.exitFullscreen();
      this.analytics.track('fullscreen_exit', {
        galleryLayout: this.analyticsLayout,
        slideIndex: this.activeIndex,
        slideCount: this.items.length,
      });
      return;
    }

    const request =
      stage.requestFullscreen?.bind(stage) ??
      (stage as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen?.bind(
        stage
      ) ??
      (stage as HTMLElement & { msRequestFullscreen?: () => Promise<void> }).msRequestFullscreen?.bind(stage);

    request?.()?.then(() => {
      this.analytics.track('fullscreen_enter', {
        galleryLayout: this.analyticsLayout,
        slideIndex: this.activeIndex,
        slideCount: this.items.length,
      });
    }).catch(() => undefined);
  }

  public close(): void {
    this.closeLightbox.emit();
  }

  public prev(): void {
    if (!this.hasMultipleItems) {
      return;
    }
    this.stopAutoplay();
    this.goToIndex(this.normalizeIndex(this.activeIndex - 1), 'prev');
  }

  public next(): void {
    if (!this.hasMultipleItems) {
      return;
    }
    this.stopAutoplay();
    this.goToIndex(this.normalizeIndex(this.activeIndex + 1), 'next');
  }

  public startAutoplay(): void {
    if (!this.hasMultipleItems) return;
    this.stopAutoplay();
    this.isAutoplaying = true;
    this.autoplayTimer = setInterval(() => {
      this.goToIndex(this.normalizeIndex(this.activeIndex + 1), 'next');
      this.cdr.markForCheck();
    }, this.autoplayInterval);
    this.cdr.markForCheck();
  }

  public stopAutoplay(): void {
    if (this.autoplayTimer !== null) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.isAutoplaying = false;
    this.cdr.markForCheck();
  }

  public toggleAutoplay(): void {
    if (this.isAutoplaying) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  public openAt(index: number): void {
    const target = this.normalizeIndex(index);
    if (target === this.activeIndex) {
      return;
    }

    const direction: SlideDirection =
      target === this.normalizeIndex(this.activeIndex + 1)
        ? 'next'
        : target === this.normalizeIndex(this.activeIndex - 1)
          ? 'prev'
          : 'none';

    this.goToIndex(target, direction);
  }

  public onImageLoad(): void {
    this.imageLoading = false;
    this.cdr.markForCheck();
  }

  public onImageError(): void {
    this.imageLoading = false;
    this.cdr.markForCheck();
  }

  public onMediaTransitionEnd(event: TransitionEvent): void {
    if (!this.fading || event.propertyName !== 'opacity' || this.pendingIndex === null) {
      return;
    }

    const target = this.pendingIndex;
    this.pendingIndex = null;
    this.indexChange.emit(target);
    this.updateSlideAnnouncement();

    requestAnimationFrame(() => {
      this.fading = false;
      this.slideDirection = 'none';
      this.cdr.markForCheck();
    });
  }

  public onImageWheel(event: WheelEvent): void {
    if (!this.lightboxItem || this.lightboxItem._isText || this.lightboxItem._isVideo) {
      return;
    }

    if (!event.ctrlKey && !event.metaKey) {
      return;
    }

    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.12 : 0.12;
    this.imageScale = this.clampScale(this.imageScale + delta);
    this.cdr.markForCheck();
  }

  public onZoomTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 2) {
      return;
    }

    this.pinchStartDistance = this.getTouchDistance(event);
    this.pinchStartScale = this.imageScale;
  }

  public onZoomTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 2 || !this.pinchStartDistance) {
      return;
    }

    event.preventDefault();
    const distance = this.getTouchDistance(event);
    const ratio = distance / this.pinchStartDistance;
    this.imageScale = this.clampScale(this.pinchStartScale * ratio);
    this.cdr.markForCheck();
  }

  public onZoomTouchEnd(): void {
    this.pinchStartDistance = 0;
    if (this.imageScale <= 1.05) {
      this.resetImageZoom();
    }
  }

  public onZoomDoubleClick(): void {
    this.imageScale = this.imageScale > 1 ? 1 : 2;
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  public onEsc(): void {
    if (this.isFullscreen || this.isStageFullscreen() || this.immersiveMode) {
      if (this.immersiveMode) {
        this.immersiveMode = false;
        this.isFullscreen = false;
        this.analytics.track('fullscreen_exit', {
          galleryLayout: this.analyticsLayout,
          slideIndex: this.activeIndex,
          slideCount: this.items.length,
        });
        this.cdr.markForCheck();
        return;
      }

      this.exitFullscreen();
      this.analytics.track('fullscreen_exit', {
        galleryLayout: this.analyticsLayout,
        slideIndex: this.activeIndex,
        slideCount: this.items.length,
      });
      return;
    }

    this.close();
  }

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  public onFullscreenChange(): void {
    this.syncFullscreenState();
  }

  @HostListener('document:keydown.arrowLeft')
  public onLeft(): void {
    this.prev();
  }

  @HostListener('document:keydown.arrowRight')
  public onRight(): void {
    this.next();
  }

  @HostListener('document:keydown.home', ['$event'])
  public onHome(event: Event): void {
    if (!this.hasMultipleItems) {
      return;
    }

    (event as KeyboardEvent).preventDefault();
    this.goToIndex(0, 'none');
  }

  @HostListener('document:keydown.end', ['$event'])
  public onEnd(event: Event): void {
    if (!this.hasMultipleItems) {
      return;
    }

    (event as KeyboardEvent).preventDefault();
    this.goToIndex(this.items.length - 1, 'none');
  }

  @HostListener('document:keydown.space', ['$event'])
  public onSpace(event: Event): void {
    if (!this.hasMultipleItems) {
      return;
    }

    const ke = event as KeyboardEvent;
    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName?.toLowerCase();

    if (tag === 'button' || tag === 'video' || active?.isContentEditable) {
      return;
    }

    ke.preventDefault();
    this.next();
  }

  public onTouchStart(event: TouchEvent): void {
    if (event.touches.length > 1) {
      return;
    }

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  public onTouchEnd(event: TouchEvent): void {
    if (!this.hasMultipleItems || event.touches.length > 0 || this.imageScale > 1) {
      return;
    }

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;
    if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
      deltaX > 0 ? this.prev() : this.next();
    }
  }

  private goToIndex(index: number, direction: SlideDirection): void {
    if (this.fading || index === this.activeIndex) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.pauseAllVideos();
      this.slideDirection = 'none';
      this.resetImageZoom();
      this.indexChange.emit(index);
      this.updateSlideAnnouncement();
      this.resetImageLoadingState();
      this.cdr.markForCheck();
      return;
    }

    this.pendingIndex = index;
    this.slideDirection = direction;
    this.fading = true;
    this.resetImageZoom();
    this.cdr.markForCheck();
  }

  private normalizeIndex(index: number): number {
    if (!this.items.length) {
      return 0;
    }

    return (index + this.items.length) % this.items.length;
  }

  private preloadImages(): void {
    const indices =
      this.items.length <= PRELOAD_ALL_THRESHOLD
        ? this.items.map((_, i) => i)
        : [
            this.activeIndex,
            this.normalizeIndex(this.activeIndex - 1),
            this.normalizeIndex(this.activeIndex + 1),
          ];

    const seen = new Set<number>();
    indices.forEach((index) => {
      if (seen.has(index)) {
        return;
      }

      seen.add(index);
      const item = this.items[index];
      if (!item || item._isText || item._isVideo || !item._lightboxSrc) {
        return;
      }

      const image = new Image();
      image.src = item._lightboxSrc;
    });
  }

  private resetImageLoadingState(): void {
    const item = this.lightboxItem;
    this.imageLoading = !!(item && !item._isText && !item._isVideo);
  }

  private resetImageZoom(): void {
    this.imageScale = 1;
    this.pinchStartDistance = 0;
    this.pinchStartScale = 1;
  }

  private clampScale(scale: number): number {
    return Math.min(MAX_IMAGE_SCALE, Math.max(MIN_IMAGE_SCALE, scale));
  }

  private getTouchDistance(event: TouchEvent): number {
    const [a, b] = [event.touches[0], event.touches[1]];
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  private updateSlideAnnouncement(): void {
    if (!this.items.length) {
      this.slideAnnouncement = '';
      return;
    }

    const item = this.lightboxItem;
    const label = item?.title || item?.caption || item?.eyebrow || '';
    this.slideAnnouncement = label
      ? `Slide ${this.activeIndex + 1} of ${this.items.length}: ${label}`
      : `Slide ${this.activeIndex + 1} of ${this.items.length}`;
  }

  private scrollActiveThumbIntoView(): void {
    const nav = this.thumbsNavRef?.nativeElement;
    if (!nav) {
      return;
    }

    const active = nav.querySelector<HTMLElement>('.lb__thumb--active');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  private trackSlideView(isOpen: boolean): void {
    if (isOpen) {
      this.analytics.track('gallery_open', {
        galleryLayout: this.analyticsLayout,
        slideIndex: this.activeIndex,
        slideCount: this.items.length,
      });
      this.lastTrackedSlideIndex = this.activeIndex;
      return;
    }

    if (this.lastTrackedSlideIndex === this.activeIndex) {
      return;
    }

    this.lastTrackedSlideIndex = this.activeIndex;
    this.analytics.track('slide_view', {
      galleryLayout: this.analyticsLayout,
      slideIndex: this.activeIndex,
      slideCount: this.items.length,
    });
  }

  private enablePageInert(): void {
    let current: HTMLElement | null = this.hostRef.nativeElement;
    while (current?.parentElement) {
      const parent: HTMLElement = current.parentElement;
      Array.from(parent.children).forEach((sibling: Element) => {
        if (sibling !== current) {
          sibling.setAttribute('inert', '');
          this.inertedElements.push(sibling);
        }
      });
      current = parent;
    }
  }

  private disablePageInert(): void {
    this.inertedElements.forEach((element) => element.removeAttribute('inert'));
    this.inertedElements.length = 0;
  }

  private pauseAllVideos(): void {
    const host = this.dialogContainerRef?.nativeElement;
    if (!host) {
      return;
    }

    host.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  }

  private syncFullscreenState(): void {
    const next = this.isStageFullscreen();
    if (this.isFullscreen !== next) {
      this.isFullscreen = next;
      this.immersiveMode = false;
      this.cdr.markForCheck();
    }
  }

  private isStageFullscreen(): boolean {
    const stage = this.stageCanvasRef?.nativeElement;
    if (!stage) {
      return false;
    }

    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      msFullscreenElement?: Element | null;
    };

    const active =
      document.fullscreenElement ?? doc.webkitFullscreenElement ?? doc.msFullscreenElement;

    return active === stage;
  }

  private exitFullscreen(): void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void>;
      msExitFullscreen?: () => Promise<void>;
    };

    const exit =
      document.exitFullscreen?.bind(document) ??
      doc.webkitExitFullscreen?.bind(document) ??
      doc.msExitFullscreen?.bind(document);

    exit?.()?.catch(() => undefined);
  }

  private exitFullscreenIfActive(): void {
    if (this.isStageFullscreen()) {
      this.exitFullscreen();
    }
  }
}
